export const TOTAL_DIMENSION = { id: "total", label: "Total", groupingField: null, driverDimension: null, hierarchical: false, available: true, kind: "total" };

export const ANALYZER_DIMENSIONS = [
  TOTAL_DIMENSION,
  { id: "service",     label: "Service",      groupingField: "service_name",    driverDimension: "service",  hierarchical: false, available: true, kind: "flat" },
  { id: "sku",         label: "SKU",          groupingField: "sku_description", driverDimension: "sku",      hierarchical: false, available: true, kind: "flat" },
  // environment has no backing column in the cost pipeline yet → disabled (Task 1 finding).
  { id: "environment", label: "Environment",  groupingField: null,              driverDimension: null,       hierarchical: false, available: false, unavailableReason: "no data yet", kind: "flat" },
  // AI model names live in sku_description (no `model` column); backend maps ai_model → sku_description.
  { id: "ai_model",    label: "AI Model",     groupingField: "sku_description", driverDimension: "ai_model", hierarchical: false, available: true, kind: "flat" },
  // provider/usage_type are real columns on allocation_cost_base_daily and
  // are also two of the cross-dimension attribution table's ranked
  // candidates (backend internal/investigation/attribution.go); their `id`
  // must equal the attribution row's `dimension` key so a clicked row
  // resolves here via dimensionById instead of silently falling back to Total.
  { id: "provider",    label: "Provider",     groupingField: "provider",        driverDimension: "provider",   hierarchical: false, available: true, kind: "flat" },
  { id: "usage_type",  label: "Usage type",   groupingField: "usage_type",      driverDimension: "usage_type", hierarchical: false, available: true, kind: "flat" },
  // Perspectives are grouped via the allocation ownership tree (owner nodes),
  // sourced from /analyzer/perspective-series + /analyzer/perspective-tree,
  // keyed by dimensionCode rather than a cost-base groupingField (B4/B5).
  { id: "organisational", label: "Organisational", groupingField: null, driverDimension: null, hierarchical: true, available: true, kind: "perspective", dimensionCode: "organisation" },
  { id: "financial",      label: "Financial",      groupingField: null, driverDimension: null, hierarchical: true, available: true, kind: "perspective", dimensionCode: "financial" },
  { id: "technical",      label: "Technical",      groupingField: null, driverDimension: null, hierarchical: true, available: true, kind: "perspective", dimensionCode: "technical" },
  { id: "use_case",       label: "Use-case",       groupingField: null, driverDimension: null, hierarchical: true, available: true, kind: "perspective", dimensionCode: "use_case" },
];

export function dimensionById(id) {
  return ANALYZER_DIMENSIONS.find((d) => d.id === id);
}

// --- Filter-field options (Task 6) ---------------------------------------
// Built here (rather than in AddFilterControl.jsx) so both AddFilterControl.jsx and any
// other consumer (e.g. the two-pane popover) can import from this dependency-free module
// without a circular import — AddFilterControl.jsx already imports ANALYZER_DIMENSIONS
// from here, so this keeps the existing import direction intact.

// costFieldOptions — the flat, available analyzer dimensions that have a real cost-base
// column, deduped by column (SKU and AI Model both map to sku_description; SKU comes
// first in ANALYZER_DIMENSIONS so it wins the dedupe).
function costFieldOptions() {
  const seen = new Set();
  const options = [];
  for (const d of ANALYZER_DIMENSIONS) {
    if (d.kind !== "flat" || !d.available || !d.groupingField) continue;
    if (seen.has(d.groupingField)) continue;
    seen.add(d.groupingField);
    options.push({ value: d.groupingField, label: d.label, kind: "flat", dimensionCode: null });
  }
  return options;
}

// perspectiveFieldOptions — the available ownership perspectives, keyed by dimensionCode
// (there's no cost-base groupingField to dedupe/filter by).
function perspectiveFieldOptions() {
  return ANALYZER_DIMENSIONS
    .filter((d) => d.kind === "perspective" && d.available)
    .map((d) => ({ value: d.dimensionCode, label: d.label, kind: "perspective", dimensionCode: d.dimensionCode }));
}

const COST_FIELD_OPTIONS = costFieldOptions();
const PERSPECTIVE_FIELD_OPTIONS = perspectiveFieldOptions();

// FILTER_FIELD_GROUPS — the two-pane add-filter popover's left list, grouped for display.
// A group is omitted entirely if it has zero options.
export const FILTER_FIELD_GROUPS = [
  COST_FIELD_OPTIONS.length ? { group: "Cost", options: COST_FIELD_OPTIONS } : null,
  PERSPECTIVE_FIELD_OPTIONS.length ? { group: "Ownership", options: PERSPECTIVE_FIELD_OPTIONS } : null,
].filter(Boolean);

// ALL_FILTER_FIELD_OPTIONS — flat concatenation across groups, cost fields first then
// perspectives. This is what AddFilterControl.jsx re-exports as FIELD_OPTIONS.
export const ALL_FILTER_FIELD_OPTIONS = FILTER_FIELD_GROUPS.flatMap((g) => g.options);

// filterFieldByValue — resolve a filter's field value (a cost-base column, or a
// perspective's dimensionCode) back to its option, e.g. to pick the value-source or
// rebuild a chip's display. Returns undefined when the value matches no known field.
export function filterFieldByValue(value) {
  return ALL_FILTER_FIELD_OPTIONS.find((o) => o.value === value);
}

// labelForField — friendly display label for a raw cost-base grouping field
// (e.g. a deep-linked filter's `field`, which carries no label of its own).
// Finds the first dimension whose groupingField matches (sku_description is
// shared by "sku" and "ai_model"; "sku" comes first in ANALYZER_DIMENSIONS so
// it wins — a reasonable default since a deep-linked filter field alone can't
// disambiguate the two). Falls back to the raw field when no dimension claims it.
export function labelForField(field) {
  const dim = ANALYZER_DIMENSIONS.find((d) => d.groupingField === field);
  return dim ? dim.label : field;
}

// describeUsageType — raw billing usage-unit strings ("count", "bytes_billed",
// "gibibyte hour") are meaningless without context, so map the common ones to a readable
// label that names the resource + unit. Unknown values are de-underscored + capitalised.
const USAGE_TYPE_DESCRIPTIONS = {
  "count": "Operations (count)",
  "requests": "Requests",
  "request": "Requests",
  "bytes": "Data (bytes)",
  "bytes_billed": "Data billed (bytes)",
  "byte_seconds": "Memory (byte·seconds)",
  "byte-seconds": "Memory (byte·seconds)",
  "seconds": "Runtime (seconds)",
  "second": "Runtime (seconds)",
  "hour": "Runtime (hours)",
  "hours": "Runtime (hours)",
  "day": "Runtime (days)",
  "month": "Duration (months)",
  "gibibyte": "Storage (GiB)",
  "gibibyte hour": "Memory/storage (GiB·hours)",
  "gibibyte month": "Storage (GiB·months)",
  "gibibyte second": "Memory (GiB·seconds)",
  "gibibyte day": "Storage (GiB·days)",
  "mebibyte": "Storage (MiB)",
  "tebibyte": "Storage (TiB)",
  "tebibyte month": "Storage (TiB·months)",
  "anthropic_messages": "Anthropic messages",
  "anthropic_tokens": "Anthropic tokens",
  "tokens": "Tokens",
};

export function describeUsageType(raw) {
  if (raw == null) return raw;
  const key = String(raw).trim().toLowerCase();
  if (USAGE_TYPE_DESCRIPTIONS[key]) return USAGE_TYPE_DESCRIPTIONS[key];
  const s = String(raw).replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
